import React, { useCallback, useMemo, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight,
  ArrowRight,
  Link2,
  LineChart,
  ShieldCheck,
  Sparkles,
  Tag,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts } from '../../utils/moduleHelpers';
import '../../styles/module-shared.css';
import './loyalty-module.css';

/** Display-only meta (icon / theme / default subtitle). Points & titles come from API. */
const EVENT_META = {
  trading: {
    title: 'Trading',
    subtitle: 'Per Standard Lot',
    theme: 'violet',
    Icon: LineChart,
  },
  deposits: {
    title: 'Deposits',
    subtitle: 'Per $100',
    theme: 'blue',
    Icon: Wallet,
  },
  referrals: {
    title: 'Referrals',
    subtitle: 'Per Successful Referral',
    theme: 'sky',
    Icon: Users,
  },
  promotions: {
    title: 'Promotions & Events',
    subtitle: 'Limited-time offers',
    theme: 'amber',
    Icon: Tag,
  },
  createdaccount: {
    title: 'Account Verification',
    subtitle: 'One-time bonus',
    theme: 'orange',
    Icon: ShieldCheck,
  },
  transfer: {
    title: 'Transfer',
    subtitle: 'Points transfer activity',
    theme: 'indigo',
    Icon: ArrowLeftRight,
    varies: true,
  },
};

const FALLBACK_META = [
  EVENT_META.trading,
  EVENT_META.deposits,
  EVENT_META.referrals,
  EVENT_META.promotions,
  EVENT_META.createdaccount,
  EVENT_META.transfer,
];

const THEMES = ['violet', 'blue', 'sky', 'amber', 'orange', 'indigo'];

const normalizeKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const formatEventTitle = (name) => {
  const raw = String(name || '').trim();
  if (!raw) return 'Earn method';
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const resolveMeta = (event, index) => {
  const key = normalizeKey(event.name);
  if (EVENT_META[key]) return EVENT_META[key];
  if (key.includes('promot')) return EVENT_META.promotions;
  if (key.includes('creat') || key.includes('verif') || key.includes('account')) {
    return EVENT_META.createdaccount;
  }
  if (key.includes('trad')) return EVENT_META.trading;
  if (key.includes('deposit')) return EVENT_META.deposits;
  if (key.includes('refer')) return EVENT_META.referrals;
  if (key.includes('transfer')) return EVENT_META.transfer;
  return {
    ...FALLBACK_META[index % FALLBACK_META.length],
    theme: THEMES[index % THEMES.length],
    title: formatEventTitle(event.name),
  };
};

const LoyaltyEarn = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      // Primary: GET /Loyalty/GetLoyaltyEventsList
      const res = await apiService.getLoyaltyEventsList();
      if (cancelled()) return;
      if (!res.success) {
        setError(res.error || 'Failed to load earn rules');
        setEvents([]);
        return;
      }
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('loyalty-earn', fetchData, true);

  const cards = useMemo(
    () =>
      events.map((event, index) => {
        const meta = resolveMeta(event, index);
        const pts = Number(event.points ?? 0);
        const title = formatEventTitle(event.name) || meta.title;
        const subtitle = event.description || meta.subtitle;
        const isVaries = Boolean(meta.varies) || pts === 0;
        return {
          id: event.id || index,
          title,
          subtitle,
          theme: meta.theme,
          Icon: meta.Icon,
          pointsLabel: isVaries ? 'Varies' : `${formatPts(pts)} PTS`,
        };
      }),
    [events]
  );

  return (
    <div className="page lp-page le-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Sparkles}
          title="How You Earn Loyalty Points"
          subtitle="See every way you can collect points across trading and account activity."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color="#8b0000" />
          </div>
        ) : (
          <>
            <section className="le-hero">
              <div className="le-hero__copy">
                <span className="le-hero__eyebrow">
                  <Zap className="w-3.5 h-3.5" />
                  Earning Guide
                </span>
                <h2>Turn every action into rewards</h2>
                <p>
                  Loyalty points are credited automatically when you trade, fund your account, refer
                  friends, and join promotions.
                </p>
              </div>
              <div className="le-hero__stat">
                <Link2 className="w-5 h-5" />
                <strong>{cards.length}</strong>
                <span>Ways to earn</span>
              </div>
            </section>

            {cards.length === 0 ? (
              <div className="module-card" style={{ padding: '2.5rem 1rem', textAlign: 'center' }}>
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  No earn rules available yet.
                </p>
              </div>
            ) : (
              <div className="le-grid">
                {cards.map((card) => {
                  const Icon = card.Icon;
                  return (
                    <article key={card.id} className={`le-card theme-${card.theme}`}>
                      <div className="le-card__top">
                        <div className="le-card__icon">
                          <Icon className="w-5 h-5" strokeWidth={1.85} />
                        </div>
                        <span className="le-card__pts">{card.pointsLabel}</span>
                      </div>
                      <h3>{card.title}</h3>
                      <p>{card.subtitle}</p>
                    </article>
                  );
                })}
              </div>
            )}

            <section className="le-cta">
              <div className="le-cta__copy">
                <div className="le-cta__icon">
                  <Sparkles className="w-4 h-4" />
                </div>
                <p>
                  <strong>Maximize your points</strong>
                  <span>
                    {' '}
                    Stack trading activity with limited-time promotions to grow your balance faster
                    and unlock higher loyalty tiers.
                  </span>
                </p>
              </div>
              <Link className="le-cta__btn" to="/loyalty-points">
                View Dashboard
                <ArrowRight className="w-4 h-4" />
              </Link>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default LoyaltyEarn;
